import React from 'react'
import PropTypes from 'prop-types'
import { withErrorBoundaryNoRender } from 'lib/errorBoundary'
import { flowRight } from 'lodash'
import { Switch, useLocation } from 'react-router-dom'
import { isLoggedIn } from 'lib/utils/store/currentUser'
import { EVENT_TYPE } from 'lib/utils/store/event'
import { EVENT_FORM_MODE } from 'components/Events/EventForm/EventForm'
import { VEHICLE_SELECT_ACTIONS } from 'components/Vehicles/VehicleSelection/VehicleSelection'
import { AD_STATUS } from 'lib/ad'
import { VEHICLE_STATUS } from 'lib/utils/store/vehicle'

import ModalRoute from './ModalRoute'
import { ModalRouterProvider } from './ModalRouterContext'
import ModalPrivateRoute from './ModalPrivateRoute'
import EventModal from 'components/Events/EventModal/EventModal'
import EventReminderSelectModal from 'components/Events/EventReminderSelectModal/EventReminderSelectModal'
import BosShareModal from 'components/BillOfSale/BosShare/BosShareModal/BosShareModal'
import VehiclePriceEstimateModal from 'components/Vehicles/VehiclePriceEstimate/VehiclePriceEstimateModal/VehiclePriceEstimateModal'
import VehiclePicturesModal from 'components/Vehicles/VehicleProfile/VehiclePicturesModal/VehiclePicturesModal'
import VehicleSelectionModal from 'components/Vehicles/VehicleSelection/VehicleSelectionModal'
import PurchaseInformationModal from 'components/Vehicles/VehicleProfile/VehicleProfileMarkets/PurchaseInformation/PurchaseInformationModal'
import VehicleDeleteModal from 'components/Vehicles/VehicleProfile/VehicleDeleteModal/VehicleDeleteModal'
import AdStatusModal from 'components/Ads/AdStatusModal/AdStatusModal'
import VehicleStatusModal from 'components/Vehicles/VehicleStatus/VehicleStatusModal/VehicleStatusModal'
import VehicleProfileShareModal from 'components/Vehicles/VehicleProfile/VehicleProfileShare/VehicleProfileShareModal'
import TimelineFiltersModal from 'components/Timeline/TimelineFilters/TimelineFiltersModal/TimelineFiltersModal'
import BosProcessInfoModal from './BosProcessInfoModal/BosProcessInfoModal'
import VehicleTypeChangeModal from 'components/Vehicles/VehicleTypeChangeModal/VehicleTypeChangeModal'
import BosSsnChangeModal from 'components/BillOfSale/BosSsnChange/BosSsnChangeModal'
import MakeOfferDealerAdNotificationModal from 'components/Offer/MakeOffer/MakeOfferDealerAdNotification/MakeOfferDealerAdNotificationModal'
import ModalStateRender from './ModalStateRender'
import UserSyncIdentificationNameModal from 'components/User/UserSyncIdentificationName/UserSyncIdentificationNameModal/UserSyncIdentificationNameModal'
import UserIdentificationModal from 'components/User/UserIdentification/UserIdentificationModal/UserIdentificationModal'
import FinancingApplicationEditModal from 'components/Financing/FinancingApplication/FinancingApplicationEdit/FinancingApplicationEditModal/FinancingApplicationEditModal'
import FinancingInfoModal from 'components/Financing/FinancingInfo/FinancingInfoModal'
import FinancingMakeOfferSubmittedModal from 'components/Financing/FinancingMakeOfferSubmittedModal/FinancingMakeOfferSubmittedModal'
import useCurrentUser from 'lib/hooks/useCurrentUser'
import UserPhoneVerificationModal from 'components/User/UserPhoneVerification/UserPhoneVerificationModal/UserPhoneVerificationModal'
import UserTransferPhoneNumberModal from 'components/User/UserTransferPhoneNumber/UserTransferPhoneNumberModal/UserTransferPhoneNumberModal'
import UserEditAdPostDataLostWarningModal from 'components/User/UserEdit/UserEditAdPostDataLostWarning/UserEditAdPostDataLostWarningModal/UserEditAdPostDataLostWarningModal'
import AddInfoForPgwOnBoardingModal from 'components/User/UserEdit/AddInfoForPgwOnBoarding/AddInfoForPgwOnBoardingModal/AddInfoForPgwOnBoardingModal'
import LegalNameInfoModal from 'components/User/UserEdit/LegalNameInfo/LegalNameInfoModal/LegalNameInfoModal'

const eventTypes = Object.values(EVENT_TYPE).join('|').toLowerCase()
const eventFormModes = Object.values(EVENT_FORM_MODE).join('|').toLowerCase()
const vehicleSelectActions = Object.values(VEHICLE_SELECT_ACTIONS).join('|').toLowerCase()
const adStatuses = Object.values(AD_STATUS).join('|').toLowerCase()
const vehicleStatuses = Object.values(VEHICLE_STATUS).join('|').toLowerCase()

/**
 * Router for modals
 * Displays a modal based on user path
 * @param {object} props
 * @param {boolean} props.value.primaryRouteIsPrivate - Is the user already within a private route in the main application router
 */

const RoutedModals = ({ primaryRouteIsPrivate }) => {
    const currentUser = useCurrentUser()
    const location = useLocation()
    if (location.hash.indexOf('#/modal/') !== 0) return null

    const loggedIn = isLoggedIn(currentUser)

    const modalPathWithQuery = location.hash.replace('#/modal', '')
    const modalPathParts = modalPathWithQuery.split('?')
    const modalPath = modalPathParts[0]
    const modalQuery = modalPathParts[1] ? `?${modalPathParts[1]}` : ''

    const modalLocation = {
        ...location,
        pathname: modalPath,
        search: modalQuery,
        state: {
            primaryLocation: { ...location },
        }
    }

    return (
        <ModalRouterProvider value={{ loggedIn, currentUser, isModalPath: true, primaryRouteIsPrivate }}>
            <Switch location={modalLocation}>
                <ModalRoute
                    path="/tila/:id"
                    exact
                    component={ModalStateRender}
                />
                <ModalPrivateRoute
                    path={[
                        `/tapahtuma/:formMode(${eventFormModes})/:vehicleId/:eventType(${eventTypes})`,
                        `/tapahtuma/:formMode(${eventFormModes})/:vehicleId/:eventId`,
                    ]}
                    component={EventModal}
                />
                <ModalPrivateRoute
                    path="/muistutustapa/:vehicleId"
                    component={EventReminderSelectModal}
                />
                <ModalPrivateRoute
                    path="/jaa/sahkoinen-kauppakirja/:bosId"
                    component={BosShareModal}
                />
                <ModalPrivateRoute
                    path="/ajoneuvo/:vehicleId/hinta-arvio"
                    component={VehiclePriceEstimateModal}
                />
                <ModalPrivateRoute
                    path="/ajoneuvo/:vehicleId/myyntitieto"
                    component={PurchaseInformationModal}
                />
                <ModalPrivateRoute
                    path={`/ajoneuvo/:vehicleId/ajoneuvon-tila/:vehicleStatus(${vehicleStatuses})`}
                    exact
                    component={VehicleStatusModal}
                />
                <ModalPrivateRoute
                    path={`/ajoneuvo/:vehicleId/ajoneuvon-tila/:vehicleStatus(${vehicleStatuses})/muokkaa-tilaa`}
                    exact
                    render={(props) => <VehicleStatusModal {...props} soldInfoEditMode />}
                />
                <ModalPrivateRoute
                    path="/ajoneuvo/:vehicleId/ilmoituksen-tila"
                    exact
                    component={AdStatusModal}
                />
                <ModalPrivateRoute
                    path={`/ajoneuvo/:vehicleId/ilmoituksen-tila/:adStatus(${adStatuses})`}
                    exact
                    component={AdStatusModal}
                />
                <ModalPrivateRoute
                    path="/ajoneuvo/:vehicleId/kuvat"
                    exact
                    component={VehiclePicturesModal}
                />
                <ModalPrivateRoute
                    path="/ajoneuvo/:vehicleId/poista"
                    exact
                    component={VehicleDeleteModal}
                />
                <ModalPrivateRoute
                    path="/ajoneuvo/:vehicleId/kuvat/muokkaa"
                    exact
                    render={(props) => <VehiclePicturesModal {...props} editMode />}
                />
                <ModalRoute
                    path={`/ajoneuvon-valinta/:action(${vehicleSelectActions})`}
                    exact
                    component={VehicleSelectionModal}
                />
                <ModalPrivateRoute
                    path="/ajoneuvo/:vehicleId/jaa"
                    exact
                    component={VehicleProfileShareModal}
                />
                <ModalPrivateRoute
                    path="/ajoneuvo/:vehicleId/suodata-tapahtumat"
                    exact
                    component={TimelineFiltersModal}
                />
                <ModalPrivateRoute
                    path="/ajoneuvo/:vehicleId/muokkaa-tyyppia"
                    exact
                    component={VehicleTypeChangeModal}
                />
                <ModalPrivateRoute
                    path="/kayttaja/tunnistus/:context(bos-documents)"
                    exact
                    component={UserIdentificationModal}
                />
                <ModalRoute
                    path="/financing/contract/info"
                    exact
                    component={FinancingInfoModal}
                />
                <ModalPrivateRoute
                    path="/kayttaja/yhdenmukaista-tiedot"
                    exact
                    component={UserSyncIdentificationNameModal}
                />
                <ModalRoute
                    path="/sahkoinen-kauppakirja/kaupanteon-vaiheet/:processStateId/:currentUserRole"
                    exact
                    component={BosProcessInfoModal}
                />
                <ModalPrivateRoute
                    path="/sahkoinen-kauppakirja/:bosId/henkilotunnuksen-muutos/:who(seller|owner)"
                    exact
                    component={BosSsnChangeModal}
                />
                <ModalRoute
                    path="/tarjous/autoliike-myy-kohdetta/:vertical/:adId"
                    exact
                    component={MakeOfferDealerAdNotificationModal}
                />
                <ModalRoute
                    path="/offer/submitted/financed"
                    exact
                    component={FinancingMakeOfferSubmittedModal}
                />
                <ModalPrivateRoute
                    path="/financing/application/:financingApplicationId/edit"
                    exact
                    component={FinancingApplicationEditModal}
                />
                <ModalRoute
                    path="/kayttaja/puhelinnumero/vahvista"
                    exact
                    component={UserPhoneVerificationModal}
                />
                <ModalRoute
                    path="/kayttaja/puhelinnumero/siirra"
                    exact
                    component={UserTransferPhoneNumberModal}
                />
                <ModalRoute
                    path="/kayttaja/puhelinnumero/siirra/varattu"
                    exact
                    component={UserTransferPhoneNumberModal}
                />
                <ModalRoute
                    path="/ilmoituksen-jatto-varoitus-tietojen-menettamisesta"
                    exact
                    component={UserEditAdPostDataLostWarningModal}
                />
                <ModalRoute
                    path="/perustiedot"
                    exact
                    component={AddInfoForPgwOnBoardingModal}
                />
                <ModalRoute
                    path="/nimitiedot"
                    exact
                    component={LegalNameInfoModal}
                />
            </Switch>
        </ModalRouterProvider>
    )
}

RoutedModals.propTypes = {
    primaryRouteIsPrivate: PropTypes.bool.isRequired,
}

export default flowRight(
    withErrorBoundaryNoRender,
)(RoutedModals)
